/**
 * §E · Market & Customer — AI Suggest builders/mappers.
 *
 * Consumes the shared adapter (`@/services/brandBibleAiSuggest`). Does not
 * call the legacy prompt/kind body (422). Competitive Set is §F and is out of
 * scope. Market notes `icp_note` / `seasonality_note` are first-class targets
 * on the profile button so they always appear as a draft or a refusal.
 */
import type { BuyerPersona } from '@/types/tenant';
import type {
  AISuggestRequest,
  AISuggestResponse,
  ContextItem,
  FieldDraft,
  FieldRefusal,
  TargetFieldMapping,
} from '@/services/brandBibleAiSuggest';
import { isValidUnverifiedDraft } from '@/services/brandBibleAiSuggest';

export const SECTION_E_CODE = 'E' as const;

export const DEFAULT_PERSONA: BuyerPersona = {
  segment_name: '',
  demographics: '',
  pain_points: [],
  objections: [],
};

export type PersonaBoundField = 'segment_name' | 'demographics' | 'pain_points' | 'objections';

export interface PersonaSuggestionTarget {
  /** 0-based index into the current personas array, or `'new'` to append. */
  personaIndex: number | 'new';
  field: PersonaBoundField;
  value: string;
}

/** Human-audited inventory of every editable §E control. Coverage tests
 *  assert the three buttons partition this set — no silent gaps. */
export const SECTION_E_EDITABLE_FIELDS = [
  'customer_pain_points',
  'ideal_client',
  'icp_note',
  'seasonality_note',
  'buyer_personas.segment_name',
  'buyer_personas.demographics',
  'buyer_personas.pain_points',
  'buyer_personas.objections',
] as const;

export type SectionEEditableField = (typeof SECTION_E_EDITABLE_FIELDS)[number];

/** Legacy draft scalars that still exist on BrandBibleDraft but have no §E
 *  editor. They are context-only (or already have a v2 chunk home) — never
 *  silent suggest targets. */
export const SECTION_E_EXPLICIT_REFUSALS = [
  {
    field: 'website_audience',
    reason: 'legacy scalar — context only; no §E editor',
  },
  {
    field: 'seasonality',
    reason: 'legacy scalar — v2 home is seasonality_note chunks',
  },
] as const;

export const SECTION_E_SUGGESTION_GROUPS = [
  {
    id: 'pain-points',
    sectionId: 'audience',
    label: 'Suggest pain points',
    fields: ['customer_pain_points'] as const,
  },
  {
    id: 'profile-notes',
    sectionId: 'audience_profile',
    label: 'Suggest profile',
    fields: ['ideal_client', 'icp_note', 'seasonality_note'] as const,
  },
  {
    id: 'persona-details',
    sectionId: 'audience_personas',
    label: 'Suggest persona details',
    fields: [
      'buyer_personas.segment_name',
      'buyer_personas.demographics',
      'buyer_personas.pain_points',
      'buyer_personas.objections',
    ] as const,
  },
] as const;

export type SectionEGroupId = (typeof SECTION_E_SUGGESTION_GROUPS)[number]['id'];

const LOCAL_REFUSAL_SOURCE = 'refusal — no grounded context yet';
const CANNED_SOURCE_RE = /guided defaults|demo data/i;
const PERSONA_TARGET_RE =
  /^persona_(new|\d+)_(segment_name|demographics|pain_points|objections)$/;
const PERSONA_FIELD_ORDER: PersonaBoundField[] = [
  'demographics',
  'pain_points',
  'objections',
  'segment_name',
];
const MAX_TARGETS = 9;
const MAX_CONTEXT = 24;

export function boundShortList(values: string[]): string[] {
  return values
    .map((v) => v.trim())
    .filter(Boolean)
    .map((v) => v.slice(0, 400))
    .slice(0, 20);
}

export function splitPainPoints(raw: string | undefined): string[] {
  return (raw ?? '')
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function parsePersonaTargetField(
  targetField: string,
): Omit<PersonaSuggestionTarget, 'value'> | null {
  const m = PERSONA_TARGET_RE.exec(targetField);
  if (!m) return null;
  const personaIndex: number | 'new' = m[1] === 'new' ? 'new' : Number(m[1]);
  if (personaIndex !== 'new' && !Number.isInteger(personaIndex)) return null;
  return { personaIndex, field: m[2] as PersonaBoundField };
}

/**
 * Identity-bound persona apply. Never falls back to "last / next empty slot".
 * Unknown or out-of-range indexes are a no-op so out-of-order chips cannot
 * mutate the wrong persona.
 */
export function applyPersonaSuggestion(
  personas: BuyerPersona[],
  target: PersonaSuggestionTarget,
): BuyerPersona[] {
  const value = target.value.trim();
  if (!value) return personas;

  if (target.personaIndex === 'new') {
    if (target.field !== 'segment_name') return personas;
    return [...personas, { ...DEFAULT_PERSONA, segment_name: value }];
  }

  const idx = target.personaIndex;
  if (!Number.isInteger(idx) || idx < 0 || idx >= personas.length) return personas;

  const current = personas[idx];
  const next = [...personas];
  if (target.field === 'pain_points') {
    next[idx] = { ...current, pain_points: [...(current.pain_points ?? []), value] };
  } else if (target.field === 'objections') {
    next[idx] = { ...current, objections: [...(current.objections ?? []), value] };
  } else if (target.field === 'demographics') {
    next[idx] = { ...current, demographics: value };
  } else if (target.field === 'segment_name') {
    next[idx] = { ...current, segment_name: value };
  } else {
    return personas;
  }
  return next;
}

export interface AudienceSuggestInput {
  businessName?: string;
  idealClient?: string;
  customerPainPoints?: string;
  websiteAudience?: string;
  icpNotes?: string[];
  seasonalityNotes?: string[];
  personas?: BuyerPersona[];
}

function pushContext(out: ContextItem[], key: string, raw: string | undefined): void {
  const value = (raw ?? '').trim().slice(0, 1500);
  if (!value) return;
  if (out.some((item) => item.key === key)) return;
  if (out.length >= MAX_CONTEXT) return;
  out.push({ key, value });
}

export function buildSectionEContext(input: AudienceSuggestInput): {
  tenant_context: ContextItem[];
  section_context: ContextItem[];
} {
  const tenant_context: ContextItem[] = [];
  const section_context: ContextItem[] = [];
  pushContext(tenant_context, 'business_name', input.businessName);
  pushContext(section_context, 'ideal_client', input.idealClient);
  pushContext(section_context, 'customer_pain_points', input.customerPainPoints);
  pushContext(section_context, 'website_audience', input.websiteAudience);
  (input.icpNotes ?? []).forEach((note, i) => {
    pushContext(section_context, i === 0 ? 'icp_note' : `icp_note_${i + 1}`, note);
  });
  (input.seasonalityNotes ?? []).forEach((note, i) => {
    pushContext(section_context, i === 0 ? 'seasonality_note' : `seasonality_note_${i + 1}`, note);
  });
  (input.personas ?? []).forEach((p, i) => {
    pushContext(section_context, `persona_${i}_segment_name`, p.segment_name);
    pushContext(section_context, `persona_${i}_demographics`, p.demographics);
    pushContext(section_context, `persona_${i}_pain_points`, (p.pain_points ?? []).filter(Boolean).join('; '));
    pushContext(section_context, `persona_${i}_objections`, (p.objections ?? []).filter(Boolean).join('; '));
  });
  return { tenant_context, section_context };
}

function target(partial: TargetFieldMapping): TargetFieldMapping {
  return {
    ...partial,
    existing: boundShortList(partial.existing),
    dismissed: boundShortList(partial.dismissed),
    instruction: partial.instruction.slice(0, 400),
  };
}

export function buildGroupTargets(
  groupId: SectionEGroupId,
  input: AudienceSuggestInput,
  dismissed: string[],
): TargetFieldMapping[] {
  const dismissedList = boundShortList(dismissed);

  if (groupId === 'pain-points') {
    return [
      target({
        output_key: 'customer_pain_points',
        target_field: 'customer_pain_points',
        instruction:
          'Draft customer pain points only from supplied audience context. Do not invent metrics, quotes, or competitor facts.',
        kind: 'chips',
        max_drafts: 5,
        claim_policy: 'grounded',
        existing: splitPainPoints(input.customerPainPoints),
        dismissed: dismissedList,
      }),
    ];
  }

  if (groupId === 'profile-notes') {
    return [
      target({
        output_key: 'ideal_client',
        target_field: 'ideal_client',
        instruction:
          'Draft an ideal-client profile strictly from supplied market notes and audience context. Do not invent facts.',
        kind: 'text',
        max_drafts: 3,
        claim_policy: 'grounded',
        existing: input.idealClient ? [input.idealClient] : [],
        dismissed: dismissedList,
      }),
      target({
        output_key: 'icp_note',
        target_field: 'icp_note',
        instruction:
          'Draft an ICP market note from supplied context only. Refuse if unsupported. Do not invent demographic or competitor facts.',
        kind: 'text',
        max_drafts: 3,
        claim_policy: 'grounded',
        existing: input.icpNotes ?? [],
        dismissed: dismissedList,
      }),
      target({
        output_key: 'seasonality_note',
        target_field: 'seasonality_note',
        instruction:
          'Draft a seasonality market note from supplied context only. Refuse if unsupported. Do not invent metrics or quotes.',
        kind: 'text',
        max_drafts: 3,
        claim_policy: 'grounded',
        existing: input.seasonalityNotes ?? [],
        dismissed: dismissedList,
      }),
    ];
  }

  const personas = input.personas ?? [];
  const out: TargetFieldMapping[] = [
    target({
      output_key: 'persona_new_segment_name',
      target_field: 'persona_new_segment_name',
      instruction:
        'Suggest a new buyer-persona segment name only if implied by supplied context. Do not invent demographics.',
      kind: 'chips',
      max_drafts: 3,
      claim_policy: 'grounded',
      existing: personas.map((p) => p.segment_name).filter(Boolean),
      dismissed: dismissedList,
    }),
  ];

  for (let i = 0; i < personas.length && out.length < MAX_TARGETS; i += 1) {
    const persona = personas[i];
    for (const field of PERSONA_FIELD_ORDER) {
      if (out.length >= MAX_TARGETS) break;
      const key = `persona_${i}_${field}`;
      const existing =
        field === 'pain_points' || field === 'objections'
          ? persona[field] ?? []
          : field === 'demographics'
            ? persona.demographics
              ? [persona.demographics]
              : []
            : persona.segment_name
              ? [persona.segment_name]
              : [];
      out.push(
        target({
          output_key: key,
          target_field: key,
          instruction:
            field === 'demographics'
              ? `Draft demographics for existing persona index ${i} only if grounded in context. Refuse rather than invent. Leave unverified.`
              : `Draft ${field.replace('_', ' ')} for existing persona index ${i} only from supplied context. Do not invent facts.`,
          kind: 'chips',
          max_drafts: 3,
          claim_policy: 'grounded',
          existing,
          dismissed: dismissedList,
        }),
      );
    }
  }
  return out;
}

export function buildAudienceRequest(
  groupId: SectionEGroupId,
  input: AudienceSuggestInput,
  dismissed: string[],
): AISuggestRequest {
  const group = SECTION_E_SUGGESTION_GROUPS.find((g) => g.id === groupId)!;
  const { tenant_context, section_context } = buildSectionEContext(input);
  return {
    section: SECTION_E_CODE,
    section_id: group.sectionId,
    prompt:
      'Section E audience drafts. Use only supplied context. Do not invent demographic facts, competitor facts, quotes, or metrics.',
    targets: buildGroupTargets(groupId, input, dismissed),
    tenant_context,
    section_context,
  };
}

export function contextIsEmpty(request: Pick<AISuggestRequest, 'tenant_context' | 'section_context'>): boolean {
  return request.tenant_context.length === 0 && request.section_context.length === 0;
}

export function localRefusalResult(targets: TargetFieldMapping[], reason: string): MappedSuggestResult {
  return {
    drafts: [],
    refusals: targets.map((t) => ({
      output_key: t.output_key,
      target_field: t.target_field,
      reason,
      unsafe_categories: [],
    })),
    source: reason.startsWith('refusal') ? reason : `refusal — ${reason}`,
    provenanceUnverified: true,
  };
}

export function isCannedSource(source?: string): boolean {
  return typeof source === 'string' && CANNED_SOURCE_RE.test(source);
}

/** Honest model provenance is the server envelope source, never a canned label. */
export function isHonestModelSource(source?: string): boolean {
  if (!source || isCannedSource(source)) return false;
  const s = source.toLowerCase();
  if (s.startsWith('refusal')) return false;
  return s.startsWith('ai draft') || s.includes('unverified') || s.startsWith('model:');
}

export interface MappedSuggestResult {
  drafts: FieldDraft[];
  refusals: FieldRefusal[];
  source: string;
  provenanceUnverified: boolean;
}

export function mapSuggestResponse(
  response: AISuggestResponse,
  allowedTargetFields: Set<string>,
): MappedSuggestResult {
  if (isCannedSource(response.source) || !isHonestModelSource(response.source)) {
    return localRefusalResult(
      [...allowedTargetFields].map((target_field) => ({
        output_key: target_field,
        target_field,
        instruction: 'x',
        kind: 'text',
        max_drafts: 1,
        claim_policy: 'grounded',
        existing: [],
        dismissed: [],
      })),
      'refusal — model unavailable, canned source rejected',
    );
  }

  const drafts: FieldDraft[] = [];
  for (const draft of response.drafts ?? []) {
    if (!allowedTargetFields.has(draft.target_field) && !allowedTargetFields.has(draft.output_key)) continue;
    if (!isValidUnverifiedDraft(draft)) continue;
    drafts.push(draft);
  }

  const refusals: FieldRefusal[] = [];
  for (const refusal of response.refusals ?? []) {
    if (!allowedTargetFields.has(refusal.target_field) && !allowedTargetFields.has(refusal.output_key)) continue;
    refusals.push(refusal);
  }

  return {
    drafts,
    refusals,
    source: response.source,
    provenanceUnverified: true,
  };
}

export function allowedFieldsForTargets(targets: TargetFieldMapping[]): Set<string> {
  return new Set(targets.flatMap((t) => [t.output_key, t.target_field]));
}

export function personaDraftLabel(draft: FieldDraft, personas: BuyerPersona[]): string {
  const parsed = parsePersonaTargetField(draft.target_field) ?? parsePersonaTargetField(draft.output_key);
  if (!parsed) return draft.value;
  if (parsed.personaIndex === 'new') return `Segment · ${draft.value}`;
  const idx = parsed.personaIndex;
  const name = personas[idx]?.segment_name?.trim() || `Persona ${idx + 1}`;
  const fieldLabel: Record<PersonaBoundField, string> = {
    segment_name: 'Segment',
    demographics: 'Demographics',
    pain_points: 'Pain point',
    objections: 'Objection',
  };
  return `${name} · ${fieldLabel[parsed.field]} · ${draft.value}`;
}

export const EMPTY_CONTEXT_REFUSAL = LOCAL_REFUSAL_SOURCE;
export const MISSING_TENANT_REFUSAL = 'refusal — tenant_id required';
